import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Select, { components as selectComponents } from 'react-select';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE_URL } from '../config/api';
import { showToast } from '../utils/toast';
import html2canvas from 'html2canvas';
import './Trening.css';

const createEmptyRow = () => ({
  exercise: '',
  sets: '',
  reps: '',
  rest: '',
  notes: '',
  technique: 'none'
});

const createEmptyWarmupRow = () => ({
  exercise: '',
  sets: '',
  reps: '',
  advice: ''
});

const parseWarmupRowsFromServer = (workout) => {
  const exercises = parseArray(workout?.warmup_exercises);
  const sets = parseArray(workout?.warmup_sets);
  const reps = parseArray(workout?.warmup_reps);
  const advice = parseArray(workout?.warmup_advice);
  const rows = exercises.map((exercise, rowIndex) => ({
    exercise,
    sets: sets[rowIndex] || '',
    reps: reps[rowIndex] || '',
    advice: advice[rowIndex] || ''
  }));
  if (rows.length === 0) {
    rows.push(createEmptyWarmupRow());
  } else {
    rows.push(createEmptyWarmupRow());
  }
  return rows;
};

const ensureWarmupRowStructure = (rows) => {
  const filled = rows
    .filter((row) => row.exercise && row.exercise.trim())
    .map((row) => ({
      exercise: row.exercise.trim(),
      sets: row.sets || '',
      reps: row.reps || '',
      advice: row.advice || ''
    }));
  if (filled.length === 0) {
    return [createEmptyWarmupRow()];
  }
  return [...filled, createEmptyWarmupRow()];
};

const buildEmptyWarmupBySlot = (count) => {
  const map = {};
  for (let n = 1; n <= count; n += 1) {
    map[n] = { rows: [createEmptyWarmupRow()] };
  }
  return map;
};

const parseTips = (raw) => {
  if (!raw) return [];
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const normalizeTechniqueKey = (raw) => {
  const v = raw == null ? 'none' : String(raw).toLowerCase().trim();
  if (v === 'none' || v === 'drop_set' || v === 'circuit') return v;
  if (/^ss_[a-h]$/.test(v) || /^giant_[a-h]$/.test(v)) return v;
  return 'none';
};

/** @param {(path: string) => string} t */
const getTechniqueLabel = (t, key) => {
  const k = normalizeTechniqueKey(key);
  if (k === 'none') return '';
  if (k === 'drop_set') return t('trening.technique.dropSet');
  if (k === 'circuit') return t('trening.technique.circuit');
  const ss = k.match(/^ss_([a-h])$/);
  if (ss) return `${t('trening.technique.superset')} · ${ss[1].toUpperCase()}`;
  const gs = k.match(/^giant_([a-h])$/);
  if (gs) return `${t('trening.technique.giantSet')} · ${gs[1].toUpperCase()}`;
  return '';
};

const VARIATION_KEY_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const nextWorkoutVariationKey = (existingKeys) => {
  const set = new Set(existingKeys.map((k) => String(k).toUpperCase()));
  for (let i = 0; i < VARIATION_KEY_LETTERS.length; i += 1) {
    const c = VARIATION_KEY_LETTERS[i];
    if (!set.has(c)) return c;
  }
  return `V${existingKeys.length + 1}`;
};

const sortVariationKeyStrings = (keys) =>
  [...keys].sort((a, b) => {
    const sa = String(a).toUpperCase();
    const sb = String(b).toUpperCase();
    if (sa.length === 1 && sb.length === 1) return sa.localeCompare(sb);
    return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
  });

const createEmptyWorkoutVariation = (workoutNumber, variationKey) => ({
  workoutNumber,
  variationKey,
  title: '',
  advice: '',
  rows: [createEmptyRow()]
});

const parseArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseAdviceValue = (value) => {
  if (!value) return '';

  if (Array.isArray(value)) {
    return value.filter(Boolean).join('\n');
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean).join('\n');
      }
      if (typeof parsed === 'string') {
        return parsed;
      }
    } catch {
      // not JSON, fall back to raw string
    }

    return value;
  }

  return '';
};

const normalizeWorkoutFromServer = (workout, fallbackWorkoutNumber, fallbackVariationKey) => {
  const workoutNumber = Number(workout.workout_number) || fallbackWorkoutNumber;
  const variationKey = (workout.variation_key || fallbackVariationKey || 'A').toString().toUpperCase();
  const exercises = parseArray(workout.exercises);
  const sets = parseArray(workout.sets);
  const reps = parseArray(workout.reps);
  const techniques = parseArray(workout.exercise_techniques);
  const rests = parseArray(workout.exercise_rest);
  const notes = parseArray(workout.exercise_notes);
  const adviceText = parseAdviceValue(workout.pt_advice);
  const titleFromServer =
    (typeof workout.workout_title === 'string' && workout.workout_title.trim()) ||
    (typeof workout.title === 'string' && workout.title.trim()) ||
    '';

  const rows = exercises.map((exercise, rowIndex) => ({
    exercise,
    sets: sets[rowIndex] || '',
    reps: reps[rowIndex] || '',
    rest: rests[rowIndex] || '',
    notes: notes[rowIndex] || '',
    technique: normalizeTechniqueKey(techniques[rowIndex])
  }));

  if (rows.length === 0) {
    rows.push(createEmptyRow());
  } else {
    rows.push(createEmptyRow());
  }

  return {
    workoutNumber,
    variationKey,
    title: titleFromServer,
    advice: adviceText,
    rows
  };
};

const ensureRowStructure = (rows) => {
  const filled = rows
    .filter((row) => row.exercise && row.exercise.trim())
    .map((row) => ({
      exercise: row.exercise.trim(),
      sets: row.sets || '',
      reps: row.reps || '',
      rest: row.rest || '',
      notes: row.notes || '',
      technique: normalizeTechniqueKey(row.technique)
    }));

  if (filled.length === 0) {
    return [createEmptyRow()];
  }

  return [...filled, createEmptyRow()];
};

function Trening() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [exercises, setExercises] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [workoutCount, setWorkoutCount] = useState(6);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Admin-specific state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerInfo, setSelectedCustomerInfo] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedWorkoutNumber, setSelectedWorkoutNumber] = useState(1);
  const [warmupBySlot, setWarmupBySlot] = useState(() => buildEmptyWarmupBySlot(6));
  const workoutTableRef = useRef(null);

  // Template state
  const [workoutTemplates, setWorkoutTemplates] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Load-template mapping modal
  const [loadTemplateModal, setLoadTemplateModal] = useState(null); // { template, mapping: [{tmplWorkoutNumber, targetSlot, include}] }

  // Tips state
  const [workoutTips, setWorkoutTips] = useState([]);
  const [expandedTip, setExpandedTip] = useState(null);

  const multiValueComponents = useMemo(() => ({
    MultiValueLabel: (props) => (
      <selectComponents.MultiValueLabel {...props}>
        <span className="select-multi-label" title={props.data.label}>
          {props.children}
        </span>
      </selectComponents.MultiValueLabel>
    )
  }), []);

  const selectMenuProps = useMemo(() => {
    if (typeof document === 'undefined') {
      return { menuPortalTarget: null, menuPosition: undefined, styles: {} };
    }

    return {
      menuPortalTarget: document.body,
      menuPosition: 'fixed',
      styles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
      }
    };
  }, []);

  const handleDownloadWorkoutImage = async () => {
    if (!workoutTableRef.current || isDownloading) return;

    try {
      setIsDownloading(true);
      const canvas = await html2canvas(workoutTableRef.current, {
        backgroundColor: '#ffffff',
        scale: window.devicePixelRatio || 2,
        scrollX: 0,
        scrollY: -window.scrollY
      });

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = 'workout-plan.png';
      link.click();
    } catch (error) {
      console.error('Error generating workout plan image:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach';
  const targetUserId = isAdmin && selectedCustomer ? selectedCustomer.id : user?.id;

  useEffect(() => {
    fetchExercises();
    
    if (isAdmin) {
      fetchCustomers();
      fetchWorkoutTemplates();
    } else if (user) {
      setWorkoutCount(user.workout_count || 6);
      initializeWorkouts(user.workout_count || 6);
      fetchWorkoutPlans(user.id);
      axios.get(`${API_BASE_URL}/api/users/${user.id}`).then((res) => {
        setWorkoutTips(parseTips(res.data.workout_tips));
      }).catch(() => {});
    }
  }, [user, isAdmin]);

  useEffect(() => {
    if (isAdmin && selectedCustomer && selectedCustomerInfo) {
      const customerWorkoutCount = selectedCustomerInfo.workout_count || 6;
      setWorkoutCount(customerWorkoutCount);
      // Reset workouts array first, then fetch
      setWorkouts([]);
      fetchWorkoutPlans(selectedCustomer.id);
    }
  }, [selectedCustomer, selectedCustomerInfo]);

  useEffect(() => {
    setSelectedWorkoutNumber((n) => {
      const max = Math.max(1, workoutCount || 1);
      return Math.min(Math.max(1, n), max);
    });
  }, [workoutCount]);

  useEffect(() => {
    setSelectedWorkoutNumber(1);
  }, [isAdmin ? selectedCustomer?.id : user?.id]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      const customerList = response.data.filter(u => u.role === 'customer');
      setCustomers(customerList);
      
      // Get full details for customer options
      const customerOptions = await Promise.all(
        customerList.map(async (customer) => {
          try {
            const userDetails = await axios.get(`${API_BASE_URL}/api/users/${customer.id}`);
            return { 
              ...customer, 
              meal_count: userDetails.data.meal_count || 6,
              workout_count: userDetails.data.workout_count || 6
            };
          } catch {
            return { 
              ...customer, 
              meal_count: 6,
              workout_count: 6
            };
          }
        })
      );
      
      setCustomers(customerOptions);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCustomerChange = async (selectedOption) => {
    if (selectedOption) {
      setSelectedCustomer(selectedOption);
      try {
        const userDetails = await axios.get(`${API_BASE_URL}/api/users/${selectedOption.id}`);
        setSelectedCustomerInfo(userDetails.data);
        setWorkoutCount(userDetails.data.workout_count || 6);
        setWorkoutTips(parseTips(userDetails.data.workout_tips));
        // Reset workouts to empty array to trigger proper reload
        setWorkouts([]);
        setWarmupBySlot(buildEmptyWarmupBySlot(userDetails.data.workout_count || 6));
      } catch (error) {
        console.error('Error fetching customer details:', error);
      }
    } else {
      setSelectedCustomer(null);
      setSelectedCustomerInfo(null);
      setWorkouts([]);
      setWarmupBySlot(buildEmptyWarmupBySlot(6));
      setWorkoutCount(6);
      setWorkoutTips([]);
    }
  };

  const initializeWorkouts = (count) => {
    const newWorkouts = [];
    for (let workoutNumber = 1; workoutNumber <= count; workoutNumber += 1) {
      newWorkouts.push(createEmptyWorkoutVariation(workoutNumber, 'A'));
    }
    setWorkouts(newWorkouts);
    setWarmupBySlot(buildEmptyWarmupBySlot(count));
    setLoading(false);
  };

  const handleAddTip = () => {
    setWorkoutTips((prev) => [...prev, { id: Date.now(), title: '', body: '' }]);
  };

  const handleTipChange = (id, field, value) => {
    setWorkoutTips((prev) => prev.map((tip) => tip.id === id ? { ...tip, [field]: value } : tip));
  };

  const handleDeleteTip = (id) => {
    setWorkoutTips((prev) => prev.filter((tip) => tip.id !== id));
    if (expandedTip === id) setExpandedTip(null);
  };

  const fetchWorkoutTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/templates/workouts`);
      setWorkoutTemplates(res.data);
    } catch {
      // silently ignore
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      // Build the same grouped payload as handleSave
      const grouped = new Map();
      workouts.forEach((workout) => {
        const workoutNumber = Number(workout.workoutNumber) || 1;
        const key = workout.variationKey || 'A';
        if (!grouped.has(workoutNumber)) {
          grouped.set(workoutNumber, { workout_number: workoutNumber, variations: [] });
        }
        const trimmedRows = workout.rows.filter((r) => r.exercise && r.exercise.trim());
        grouped.get(workoutNumber).variations.push({
          key,
          title: workout.title?.trim() || '',
          rows: trimmedRows.map((r) => ({
            exercise: r.exercise,
            sets: r.sets || '',
            reps: r.reps || '',
            rest: r.rest || '',
            notes: r.notes || '',
            technique: normalizeTechniqueKey(r.technique)
          })),
          advice: workout.advice?.trim() || ''
        });
      });
      const data = Array.from(grouped.values()).sort((a, b) => a.workout_number - b.workout_number);
      await axios.post(`${API_BASE_URL}/api/templates/workouts`, {
        name: templateName.trim(),
        workout_count: workoutCount,
        data
      });
      showToast.success(t('templates.savedSuccess'));
      setShowSaveTemplateModal(false);
      setTemplateName('');
      fetchWorkoutTemplates();
    } catch {
      showToast.error('Error saving template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleLoadWorkoutTemplate = (option) => {
    if (!option) return;
    const tmpl = option.template;
    const tmplWorkouts = (tmpl.data || []).map((wo) => wo.workout_number);
    const mapping = tmplWorkouts.map((wn) => ({
      tmplWorkoutNumber: wn,
      targetSlot: wn <= workoutCount ? wn : 1,
      include: true
    }));
    setLoadTemplateModal({ template: tmpl, mapping });
  };

  const handleApplyLoadTemplate = () => {
    if (!loadTemplateModal) return;
    const { template: tmpl, mapping } = loadTemplateModal;
    const activeMap = {};
    mapping.forEach(({ tmplWorkoutNumber, targetSlot, include }) => {
      if (include) activeMap[tmplWorkoutNumber] = targetSlot;
    });
    const replacedSlots = new Set(Object.values(activeMap));
    const kept = workouts.filter((w) => !replacedSlots.has(w.workoutNumber));
    const added = [];
    (tmpl.data || []).forEach((wo) => {
      const targetSlot = activeMap[wo.workout_number];
      if (targetSlot == null) return;
      (wo.variations || []).forEach((v) => {
        const rows = v.rows && v.rows.length > 0
          ? [...v.rows.map((r) => ({ exercise: r.exercise || '', sets: r.sets || '', reps: r.reps || '', rest: r.rest || '', notes: r.notes || '', technique: r.technique || 'none' })), createEmptyRow()]
          : [createEmptyRow()];
        added.push({
          workoutNumber: targetSlot,
          variationKey: v.key || 'A',
          title: v.title || '',
          rows,
          advice: v.advice || ''
        });
      });
    });
    const newWorkouts = [...kept, ...added];
    if (newWorkouts.length > 0) {
      setWorkouts(newWorkouts);
    }
    setLoadTemplateModal(null);
    showToast.success(t('templates.loadedSuccess'));
  };

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/exercises`);
      // Deduplicate exercises by name (case-insensitive) as a safety measure
      const seen = new Set();
      const uniqueExercises = response.data.filter(ex => {
        const nameLower = (ex.name || '').toLowerCase().trim();
        if (seen.has(nameLower)) {
          return false;
        }
        seen.add(nameLower);
        return true;
      });
      setExercises(uniqueExercises);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  const fetchWorkoutPlans = async (userId) => {
    try {
      // Get the correct workout count first
      let userWorkoutCount = 6;
      
      if (isAdmin && selectedCustomerInfo) {
        userWorkoutCount = selectedCustomerInfo.workout_count || 6;
      } else if (user) {
        userWorkoutCount = user.workout_count || 6;
      }
      
      setWorkoutCount(userWorkoutCount);
      
      const response = await axios.get(`${API_BASE_URL}/api/workout-plans/${userId}`);

      const byKey = new Map();
      (response.data || []).forEach((workout) => {
        const workoutNumber = Number(workout.workout_number) || 1;
        const variationKey = (workout.variation_key || 'A').toString().toUpperCase();
        byKey.set(
          `${workoutNumber}-${variationKey}`,
          normalizeWorkoutFromServer(workout, workoutNumber, variationKey)
        );
      });

      const merged = [];
      const warmups = buildEmptyWarmupBySlot(userWorkoutCount);
      const serverRows = response.data || [];

      for (let workoutNumber = 1; workoutNumber <= userWorkoutCount; workoutNumber += 1) {
        const keysForSlot = sortVariationKeyStrings(
          [...byKey.keys()]
            .filter((k) => k.startsWith(`${workoutNumber}-`))
            .map((k) => k.split('-')[1])
        );
        if (keysForSlot.length === 0) {
          merged.push(createEmptyWorkoutVariation(workoutNumber, 'A'));
        } else {
          keysForSlot.forEach((variationKey) => {
            const fullKey = `${workoutNumber}-${variationKey}`;
            merged.push(
              byKey.get(fullKey) || createEmptyWorkoutVariation(workoutNumber, variationKey)
            );
          });
        }

        const rowsForSlot = serverRows.filter(
          (w) => Number(w.workout_number) === workoutNumber
        );
        const warmupSource =
          rowsForSlot.find((w) => (w.variation_key || 'A').toString().toUpperCase() === 'A') ||
          rowsForSlot[0];
        warmups[workoutNumber] = {
          rows: warmupSource ? parseWarmupRowsFromServer(warmupSource) : [createEmptyWarmupRow()]
        };
      }

      setWorkouts(merged);
      setWarmupBySlot(warmups);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching workout plans:', error);
      const userWorkoutCount = isAdmin && selectedCustomerInfo 
        ? (selectedCustomerInfo.workout_count || 6)
        : (user?.workout_count || 6);
      initializeWorkouts(userWorkoutCount);
    }
  };

  const handleWorkoutTitleChange = (workoutIndex, value) => {
    setWorkouts((prev) => {
      const updated = [...prev];
      updated[workoutIndex] = {
        ...updated[workoutIndex],
        title: value
      };
      return updated;
    });
  };

  const handleExerciseSelect = (workoutIndex, rowIndex, option) => {
    setWorkouts((prev) => {
      const updated = [...prev];
      const targetWorkout = { ...updated[workoutIndex] };
      const rows = targetWorkout.rows.map((row, idx) =>
        idx === rowIndex
          ? {
              exercise: option ? option.value : '',
              sets: option ? row.sets || '' : '',
              reps: option ? row.reps || '' : '',
              rest: option ? row.rest || '' : '',
              notes: option ? row.notes || '' : '',
              technique: option ? normalizeTechniqueKey(row.technique) : 'none'
            }
          : row
      );
      targetWorkout.rows = ensureRowStructure(rows);
      updated[workoutIndex] = targetWorkout;
      return updated;
    });
  };

  const handleRemoveExerciseRow = (workoutIndex, rowIndex) => {
    setWorkouts((prev) => {
      const updated = [...prev];
      const targetWorkout = { ...updated[workoutIndex] };
      const rows = targetWorkout.rows.filter((_, idx) => idx !== rowIndex);
      targetWorkout.rows = ensureRowStructure(rows);
      updated[workoutIndex] = targetWorkout;
      return updated;
    });
  };

  const handleSetsChange = (workoutIndex, rowIndex, value) => {
    setWorkouts((prev) => {
      const updated = [...prev];
      const targetWorkout = { ...updated[workoutIndex] };
      targetWorkout.rows = targetWorkout.rows.map((row, idx) =>
        idx === rowIndex ? { ...row, sets: value } : row
      );
      updated[workoutIndex] = targetWorkout;
      return updated;
    });
  };

  const handleRepsChange = (workoutIndex, rowIndex, value) => {
    setWorkouts((prev) => {
      const updated = [...prev];
      const targetWorkout = { ...updated[workoutIndex] };
      targetWorkout.rows = targetWorkout.rows.map((row, idx) =>
        idx === rowIndex ? { ...row, reps: value } : row
      );
      updated[workoutIndex] = targetWorkout;
      return updated;
    });
  };

  const handleRowFieldChange = (workoutIndex, rowIndex, field, value) => {
    setWorkouts((prev) => {
      const updated = [...prev];
      const targetWorkout = { ...updated[workoutIndex] };
      targetWorkout.rows = targetWorkout.rows.map((row, idx) =>
        idx === rowIndex ? { ...row, [field]: value } : row
      );
      updated[workoutIndex] = targetWorkout;
      return updated;
    });
  };

  const handleTechniqueChange = (workoutIndex, rowIndex, value) => {
    setWorkouts((prev) => {
      const updated = [...prev];
      const targetWorkout = { ...updated[workoutIndex] };
      targetWorkout.rows = targetWorkout.rows.map((row, idx) =>
        idx === rowIndex ? { ...row, technique: normalizeTechniqueKey(value) } : row
      );
      updated[workoutIndex] = targetWorkout;
      return updated;
    });
  };

  const handleAdviceChange = (workoutIndex, value) => {
    setWorkouts((prev) => {
      const updated = [...prev];
      const targetWorkout = { ...updated[workoutIndex] };
      targetWorkout.advice = value;
      updated[workoutIndex] = targetWorkout;
      return updated;
    });
  };

  const handleAddWorkoutVariation = () => {
    setWorkouts((prev) => {
      const slot = selectedWorkoutNumber;
      const keys = prev.filter((w) => w.workoutNumber === slot).map((w) => w.variationKey);
      const nk = nextWorkoutVariationKey(keys);
      let insertAt = prev.length;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].workoutNumber === slot) {
          insertAt = i + 1;
          break;
        }
      }
      const next = [...prev];
      next.splice(insertAt, 0, createEmptyWorkoutVariation(slot, nk));
      return next;
    });
  };

  const handleRemoveWorkoutVariation = (workoutIndex) => {
    setWorkouts((prev) => {
      const target = prev[workoutIndex];
      if (!target) return prev;
      const sameSlot = prev.filter((w) => w.workoutNumber === target.workoutNumber);
      if (sameSlot.length <= 1) return prev;
      return prev.filter((_, i) => i !== workoutIndex);
    });
  };

  const currentWarmupRows = useMemo(() => {
    const slot = warmupBySlot[selectedWorkoutNumber];
    return slot?.rows?.length ? slot.rows : [createEmptyWarmupRow()];
  }, [warmupBySlot, selectedWorkoutNumber]);

  const updateWarmupRows = (slotNumber, updater) => {
    setWarmupBySlot((prev) => {
      const current = prev[slotNumber] || { rows: [createEmptyWarmupRow()] };
      const nextRows = typeof updater === 'function' ? updater(current.rows) : updater;
      return { ...prev, [slotNumber]: { rows: nextRows } };
    });
  };

  const handleWarmupExerciseSelect = (rowIndex, option) => {
    updateWarmupRows(selectedWorkoutNumber, (rows) => {
      const next = rows.map((row, idx) =>
        idx === rowIndex
          ? {
              exercise: option ? option.value : '',
              sets: option ? row.sets || '' : '',
              reps: option ? row.reps || '' : '',
              advice: option ? row.advice || '' : ''
            }
          : row
      );
      return ensureWarmupRowStructure(next);
    });
  };

  const handleWarmupRemoveRow = (rowIndex) => {
    updateWarmupRows(selectedWorkoutNumber, (rows) =>
      ensureWarmupRowStructure(rows.filter((_, idx) => idx !== rowIndex))
    );
  };

  const handleWarmupSetsChange = (rowIndex, value) => {
    updateWarmupRows(selectedWorkoutNumber, (rows) =>
      rows.map((row, idx) => (idx === rowIndex ? { ...row, sets: value } : row))
    );
  };

  const handleWarmupRepsChange = (rowIndex, value) => {
    updateWarmupRows(selectedWorkoutNumber, (rows) =>
      rows.map((row, idx) => (idx === rowIndex ? { ...row, reps: value } : row))
    );
  };

  const handleWarmupAdviceChange = (rowIndex, value) => {
    updateWarmupRows(selectedWorkoutNumber, (rows) =>
      rows.map((row, idx) => (idx === rowIndex ? { ...row, advice: value } : row))
    );
  };

  const handleSave = async () => {
    if (!isAdmin) {
      alert('Only admin or coach can save workout plans');
      return;
    }

    if (!selectedCustomer) {
      alert('Please select a customer first');
      return;
    }

    const grouped = new Map();
    workouts.forEach((workout) => {
      const workoutNumber = Number(workout.workoutNumber) || 1;
      const key = workout.variationKey || 'A';
      if (!grouped.has(workoutNumber)) {
        const warmupSlot = warmupBySlot[workoutNumber] || { rows: [] };
        const warmupRows = (warmupSlot.rows || []).filter((row) => row.exercise && row.exercise.trim());
        grouped.set(workoutNumber, {
          workout_number: workoutNumber,
          warmup: { rows: warmupRows },
          variations: []
        });
      }
      const trimmedRows = workout.rows.filter((row) => row.exercise && row.exercise.trim());
      grouped.get(workoutNumber).variations.push({
        key,
        title: workout.title?.trim() || null,
        rows: trimmedRows.map((row) => ({
          exercise: row.exercise,
          sets: row.sets || '',
          reps: row.reps || '',
          rest: row.rest || '',
          notes: row.notes || '',
          technique: normalizeTechniqueKey(row.technique)
        })),
        advice: workout.advice?.trim() || ''
      });
    });

    const payload = Array.from(grouped.values()).sort(
      (a, b) => a.workout_number - b.workout_number
    );

    try {
      await axios.post(`${API_BASE_URL}/api/workout-plans/${selectedCustomer.id}`, { workouts: payload });
      const cleanTips = workoutTips.filter((tip) => tip.title.trim() || tip.body.trim());
      await axios.put(`${API_BASE_URL}/api/users/${selectedCustomer.id}/workout-tips`, { tips: cleanTips });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving workout plans:', error);
      alert('Error saving workout plans');
    }
  };

  const exerciseOptions = exercises.map(e => ({ value: e.name, label: e.name }));
  const customerOptions = customers.map(c => ({ value: c, label: c.username, id: c.id }));

  const workoutSlotOptions = useMemo(
    () =>
      Array.from({ length: Math.max(0, workoutCount || 0) }, (_, i) => {
        const num = i + 1;
        return { value: num, label: `${t('trening.workout')} ${num}` };
      }),
    [workoutCount, t]
  );

  const techniqueSelectOptions = useMemo(() => {
    const opts = [{ value: 'none', label: t('trening.technique.none') }];
    'abcdefgh'.split('').forEach((letter) => {
      opts.push({
        value: `ss_${letter}`,
        label: `${t('trening.technique.superset')} · ${letter.toUpperCase()}`
      });
    });
    opts.push({ value: 'drop_set', label: t('trening.technique.dropSet') });
    'abcdefgh'.split('').forEach((letter) => {
      opts.push({
        value: `giant_${letter}`,
        label: `${t('trening.technique.giantSet')} · ${letter.toUpperCase()}`
      });
    });
    opts.push({ value: 'circuit', label: t('trening.technique.circuit') });
    return opts;
  }, [t]);

  const visibleWorkouts = useMemo(
    () => workouts.filter((w) => w.workoutNumber === selectedWorkoutNumber),
    [workouts, selectedWorkoutNumber]
  );

  const renderWarmupTable = (readOnly) => {
    const rows = readOnly
      ? currentWarmupRows.filter((row) => row.exercise && row.exercise.trim())
      : currentWarmupRows;
    const displayRows =
      readOnly && rows.length === 0 ? [] : readOnly ? rows : currentWarmupRows;

    if (readOnly && displayRows.length === 0) {
      return null;
    }

    return (
      <div className="warmup-section">
        <h3 className="warmup-section-title">{t('trening.warmupTitle')}</h3>
        <div className="table-container warmup-table-container">
          <table className="trening-table warmup-table">
            <colgroup>
              <col className="col-warmup-exercise" />
              <col className="col-warmup-sets" />
              <col className="col-warmup-reps" />
              <col className="col-warmup-advice" />
            </colgroup>
            <thead>
              <tr>
                <th>{t('trening.warmupExercise')}</th>
                <th>{t('trening.sets')}</th>
                <th>{t('trening.reps')}</th>
                <th>{t('trening.ptAdvice')}</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, rowIndex) => (
                <tr key={`warmup-${selectedWorkoutNumber}-${rowIndex}`}>
                  <td className={readOnly ? 'exercises-cell read-only' : 'exercises-cell'}>
                    {readOnly ? (
                      <span className="exercise-display-item">{row.exercise}</span>
                    ) : (
                      <div className="exercise-row">
                        <Select
                          options={exerciseOptions}
                          value={
                            row.exercise
                              ? exerciseOptions.find((opt) => opt.value === row.exercise) || {
                                  value: row.exercise,
                                  label: row.exercise
                                }
                              : null
                          }
                          onChange={(option) => handleWarmupExerciseSelect(rowIndex, option)}
                          placeholder={t('trening.selectExercise')}
                          isClearable
                          classNamePrefix="react-select"
                          menuPortalTarget={selectMenuProps.menuPortalTarget}
                          menuPosition={selectMenuProps.menuPosition}
                          styles={selectMenuProps.styles}
                        />
                        {row.exercise && (
                          <button
                            type="button"
                            className="remove-exercise-btn"
                            onClick={() => handleWarmupRemoveRow(rowIndex)}
                            aria-label={t('trening.removeExercise')}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className={readOnly ? 'sets-cell read-only' : 'sets-cell'}>
                    {readOnly ? (
                      <span className="sets-display">{row.sets || '—'}</span>
                    ) : (
                      <input
                        type="text"
                        value={row.sets}
                        onChange={(e) => handleWarmupSetsChange(rowIndex, e.target.value)}
                        className="sets-input"
                        placeholder={row.exercise ? t('trening.sets') : t('trening.addExerciseFirst')}
                        disabled={!row.exercise}
                      />
                    )}
                  </td>
                  <td className={readOnly ? 'reps-cell read-only' : 'reps-cell'}>
                    {readOnly ? (
                      <span className="reps-display">{row.reps || '—'}</span>
                    ) : (
                      <input
                        type="text"
                        value={row.reps}
                        onChange={(e) => handleWarmupRepsChange(rowIndex, e.target.value)}
                        className="reps-input"
                        placeholder={row.exercise ? t('trening.reps') : t('trening.addExerciseFirst')}
                        disabled={!row.exercise}
                      />
                    )}
                  </td>
                  <td className={readOnly ? 'advice-cell read-only' : 'advice-cell'}>
                    {readOnly ? (
                      <span className={row.advice ? 'advice-display' : 'advice-display muted'}>
                        {row.advice || '—'}
                      </span>
                    ) : (
                      <input
                        type="text"
                        value={row.advice}
                        onChange={(e) => handleWarmupAdviceChange(rowIndex, e.target.value)}
                        className="warmup-advice-input"
                        placeholder={row.exercise ? t('trening.warmupAdvicePlaceholder') : t('trening.addExerciseFirst')}
                        disabled={!row.exercise}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile layout — the desktop table is hidden at phone width */}
        <div className="mobile-workout-cards mobile-warmup-cards">
          {readOnly ? (
            <div className="mobile-exercise-card mobile-exercise-card-readonly">
              {displayRows.map((row, i) => (
                <div key={`warmup-m-${i}`} className="mobile-exercise-row">
                  <div className="mobile-exercise-head">
                    <span className="mobile-exercise-index">{i + 1}</span>
                    <span className="mobile-exercise-name">{row.exercise}</span>
                  </div>
                  <div className="mobile-exercise-details">
                    {row.sets && String(row.sets).trim() && (
                      <span className="mobile-metric-pill">{row.sets} {t('trening.sets').toLowerCase()}</span>
                    )}
                    {row.reps && String(row.reps).trim() && (
                      <span className="mobile-metric-pill">{row.reps} {t('trening.reps').toLowerCase()}</span>
                    )}
                  </div>
                  {row.advice && <div className="mobile-exercise-advice">{row.advice}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div className="mobile-exercise-card">
              {displayRows.map((row, rowIndex) => (
                <div key={`warmup-m-${selectedWorkoutNumber}-${rowIndex}`} className="mobile-edit-exercise">
                  <div className="exercise-select-row">
                    <div>
                      <Select
                        options={exerciseOptions}
                        value={row.exercise ? exerciseOptions.find((o) => o.value === row.exercise) || { value: row.exercise, label: row.exercise } : null}
                        onChange={(option) => handleWarmupExerciseSelect(rowIndex, option)}
                        placeholder={row.exercise || rowIndex === 0 ? t('trening.selectExercise') : `+ ${t('trening.addWarmupExercise')}`}
                        isClearable
                        classNamePrefix="react-select"
                        menuPortalTarget={selectMenuProps.menuPortalTarget}
                        menuPosition={selectMenuProps.menuPosition}
                        styles={selectMenuProps.styles}
                      />
                    </div>
                    {row.exercise && (
                      <button type="button" className="remove-exercise-btn" onClick={() => handleWarmupRemoveRow(rowIndex)} aria-label={t('trening.removeExercise')}>×</button>
                    )}
                  </div>
                  {row.exercise && (
                    <>
                      <div className="mobile-row-fields mobile-row-fields-2">
                        <div className="mobile-field">
                          <label>{t('trening.sets')}</label>
                          <input type="text" value={row.sets} onChange={(e) => handleWarmupSetsChange(rowIndex, e.target.value)} placeholder={t('trening.sets')} />
                        </div>
                        <div className="mobile-field">
                          <label>{t('trening.reps')}</label>
                          <input type="text" value={row.reps} onChange={(e) => handleWarmupRepsChange(rowIndex, e.target.value)} placeholder={t('trening.reps')} />
                        </div>
                      </div>
                      <div className="mobile-field mobile-note-field">
                        <label>{t('trening.exerciseNote')}</label>
                        <textarea rows={2} value={row.advice} onChange={(e) => handleWarmupAdviceChange(rowIndex, e.target.value)} placeholder={t('trening.warmupAdvicePlaceholder')} />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading && !isAdmin) {
    return (
      <div className="container">
        <div className="card">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trening-container">
      <div className="container">
        <div className="card">
          <div className="trening-header">
            <h2>{t('trening.title')}</h2>
            <div className="header-actions">
              {isAdmin && selectedCustomer && workoutTemplates.length > 0 && (
                <Select
                  options={workoutTemplates.map(tmpl => ({ value: tmpl.id, label: tmpl.name, template: tmpl }))}
                  placeholder={t('templates.selectTemplate')}
                  onChange={handleLoadWorkoutTemplate}
                  value={null}
                  isClearable={false}
                  classNamePrefix="react-select"
                  className="template-select"
                />
              )}
              {isAdmin && selectedCustomer && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowSaveTemplateModal(true)}
                >
                  {t('templates.saveAsTemplate')}
                </button>
              )}
              {isAdmin && selectedCustomer && (
                <button onClick={handleSave} className="btn-primary">
                  {t('common.saveChanges')}
                </button>
              )}
              {saved && <span className="saved-indicator">{t('common.saved')}</span>}
            </div>
          </div>

          {showSaveTemplateModal && (
            <div className="save-template-modal-overlay" onClick={() => setShowSaveTemplateModal(false)}>
              <div className="save-template-modal" onClick={(e) => e.stopPropagation()}>
                <h3>{t('templates.saveAsTemplate')}</h3>
                <label>{t('templates.templateName')}</label>
                <input
                  type="text"
                  className="field-input"
                  placeholder={t('templates.templateNamePlaceholder')}
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveAsTemplate()}
                />
                <div className="save-template-modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowSaveTemplateModal(false)}>
                    {t('templates.cancel')}
                  </button>
                  <button type="button" className="btn-primary" onClick={handleSaveAsTemplate} disabled={savingTemplate || !templateName.trim()}>
                    {t('templates.saveTemplate')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {loadTemplateModal && (
            <div className="save-template-modal-overlay" onClick={() => setLoadTemplateModal(null)}>
              <div className="save-template-modal load-template-modal" onClick={(e) => e.stopPropagation()}>
                <h3>{t('templates.loadTemplate')}</h3>
                <p className="load-template-subtitle">
                  Izaberi koji treninzi iz šablona da zamene postojeće treninge klijenta.
                </p>
                <div className="load-template-mapping">
                  <div className="load-template-mapping-header">
                    <span>Trening iz šablona</span>
                    <span>Uključi</span>
                    <span>Zameni trening №</span>
                  </div>
                  {loadTemplateModal.mapping.map((row, idx) => (
                    <div key={row.tmplWorkoutNumber} className="load-template-mapping-row">
                      <span className="load-template-meal-label">
                        {(() => {
                          const tmplWo = (loadTemplateModal.template.data || []).find((w) => w.workout_number === row.tmplWorkoutNumber);
                          const firstTitle = tmplWo?.variations?.[0]?.title;
                          return firstTitle
                            ? `${t('trening.workout')} ${row.tmplWorkoutNumber} — ${firstTitle}`
                            : `${t('trening.workout')} ${row.tmplWorkoutNumber}`;
                        })()}
                      </span>
                      <input
                        type="checkbox"
                        className="load-template-include"
                        checked={row.include}
                        onChange={() => setLoadTemplateModal((prev) => ({
                          ...prev,
                          mapping: prev.mapping.map((r, i) => i === idx ? { ...r, include: !r.include } : r)
                        }))}
                      />
                      <select
                        className="load-template-slot-select"
                        value={row.targetSlot}
                        disabled={!row.include}
                        onChange={(e) => setLoadTemplateModal((prev) => ({
                          ...prev,
                          mapping: prev.mapping.map((r, i) => i === idx ? { ...r, targetSlot: Number(e.target.value) } : r)
                        }))}
                      >
                        {Array.from({ length: workoutCount }, (_, i) => i + 1).map((wn) => (
                          <option key={wn} value={wn}>{t('trening.workout')} {wn}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="save-template-modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setLoadTemplateModal(null)}>
                    {t('templates.cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleApplyLoadTemplate}
                    disabled={!loadTemplateModal.mapping.some((r) => r.include)}
                  >
                    {t('templates.loadTemplate')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {isAdmin ? (
            <>
              <div className="customer-selector">
                <label><strong>{t('common.selectCustomer')}</strong></label>
                <Select
                  options={customerOptions}
                  value={selectedCustomer ? { value: selectedCustomer, label: selectedCustomer.username, id: selectedCustomer.id } : null}
                  onChange={(selected) => handleCustomerChange(selected?.value || null)}
                  placeholder={t('common.chooseCustomer')}
                  isClearable
                  className="customer-select"
                  classNamePrefix="react-select"
                  components={multiValueComponents}
                  menuPortalTarget={selectMenuProps.menuPortalTarget}
                  menuPosition={selectMenuProps.menuPosition}
                  styles={selectMenuProps.styles}
                />
              </div>

              {selectedCustomer && (
                <>
                  <div className="workout-info">
                    <p>{t('common.customer')} <strong>{selectedCustomer.username}</strong></p>
                    <p>{t('trening.workoutsPerWeek')} <strong>{workoutCount}</strong></p>
                  </div>

                  {/* Saveti / Tips section */}
                  <div className="tips-section-card">
                    <div className="tips-header">
                      <h3 className="tips-title">{t('trening.tipsTitle')}</h3>
                      <button type="button" className="btn-secondary tips-add-btn" onClick={handleAddTip}>
                        + {t('trening.addTip')}
                      </button>
                    </div>
                    {workoutTips.length === 0 ? (
                      <p className="tips-empty-hint">Klikni "+ {t('trening.addTip')}" da dodaš savet za ovog klijenta.</p>
                    ) : (
                      <div className="tips-cards">
                        {workoutTips.map((tip) => (
                          <div key={tip.id} className="tip-card" onClick={() => setExpandedTip(expandedTip === tip.id ? null : tip.id)}>
                            <div className="tip-card-header">
                              <input
                                type="text"
                                className="tip-title-input"
                                placeholder={t('trening.tipTitlePlaceholder')}
                                value={tip.title}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleTipChange(tip.id, 'title', e.target.value)}
                              />
                              <button
                                type="button"
                                className="tip-delete-btn"
                                onClick={(e) => { e.stopPropagation(); handleDeleteTip(tip.id); }}
                              >
                                {t('trening.deleteTip')}
                              </button>
                            </div>
                            {expandedTip === tip.id && (
                              <textarea
                                className="tip-body-input"
                                placeholder={t('trening.tipBodyPlaceholder')}
                                value={tip.body}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleTipChange(tip.id, 'body', e.target.value)}
                                rows={4}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="customer-selector">
                    <label><strong>{t('trening.selectWorkoutSlot')}</strong></label>
                    <Select
                      options={workoutSlotOptions}
                      value={
                        workoutSlotOptions.find((o) => o.value === selectedWorkoutNumber) ||
                        workoutSlotOptions[0] ||
                        null
                      }
                      onChange={(opt) => setSelectedWorkoutNumber(opt?.value ?? 1)}
                      placeholder={t('trening.chooseWorkoutSlot')}
                      isClearable={false}
                      className="customer-select"
                      classNamePrefix="react-select"
                      menuPortalTarget={selectMenuProps.menuPortalTarget}
                      menuPosition={selectMenuProps.menuPosition}
                      styles={selectMenuProps.styles}
                    />
                  </div>

                  <div className="workout-plan-capture" ref={workoutTableRef}>
                  {renderWarmupTable(false)}

                  <div className="legend-card">
                    <h3 className="legend-title">{t('trening.legendTitle')}</h3>
                    <ul className="legend-list">
                      <li><strong>RM</strong> – {t('trening.legend.rm')}</li>
                      <li><strong>1RM / 3RM / 5RM</strong> – {t('trening.legend.rmVariants')}</li>
                      <li><strong>%1RM</strong> – {t('trening.legend.pct1rm')}</li>
                      <li><strong>AMRAP</strong> – {t('trening.legend.amrap')}</li>
                      <li><strong>RPE</strong> – {t('trening.legend.rpe')}</li>
                      <li><strong>RIR</strong> – {t('trening.legend.rir')}</li>
                      <li><strong>TUT</strong> – {t('trening.legend.tut')}</li>
                      <li><strong>ROM</strong> – {t('trening.legend.rom')}</li>
                      <li><strong>DS / DROPSET</strong> – {t('trening.legend.dropset')}</li>
                      <li><strong>BW</strong> – {t('trening.legend.bw')}</li>
                      <li><strong>SS / SUPERSET</strong> – {t('trening.legend.superset')}</li>
                      <li>{t('trening.legend.setTypeColumn')}</li>
                    </ul>
                  </div>

                  <div className="variation-toolbar">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleAddWorkoutVariation}
                    >
                      + {t('trening.addVariation')}
                    </button>
                  </div>

                  <div className="table-container">
                    <table className="trening-table">
                      <colgroup>
                        <col className="col-workout" />
                        <col className="col-exercises" />
                        <col className="col-technique" />
                        <col className="col-sets" />
                        <col className="col-reps" />
                        <col className="col-rest" />
                        <col className="col-advice" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>{t('trening.variation')}</th>
                          <th>{t('trening.exercises')}</th>
                          <th>{t('trening.exerciseTechnique')}</th>
                          <th>{t('trening.sets')}</th>
                          <th>{t('trening.reps')}</th>
                          <th>{t('trening.rest')}</th>
                          <th>{t('trening.ptAdvice')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleWorkouts.flatMap((workout) => {
                          const workoutIndex = workouts.indexOf(workout);
                          if (workoutIndex === -1) return [];
                          const rows = workout.rows;
                          return rows.map((row, rowIndex) => (
                            <tr key={`${workout.variationKey}-${rowIndex}`}>
                              {rowIndex === 0 && (
                                <td className="workout-title-cell" rowSpan={rows.length}>
                                  {visibleWorkouts.length > 1 && (
                                    <div className="variation-title-row">
                                      <button
                                        type="button"
                                        className="remove-variation-btn"
                                        onClick={() => handleRemoveWorkoutVariation(workoutIndex)}
                                      >
                                        {t('trening.removeVariation')}
                                      </button>
                                    </div>
                                  )}
                                  <input
                                    type="text"
                                    value={workout.title}
                                    onChange={(e) => handleWorkoutTitleChange(workoutIndex, e.target.value)}
                                    placeholder={`${t('trening.workout')} ${workout.workoutNumber}${workout.variationKey}`}
                                    className="variation-label-input"
                                    aria-label={t('trening.variation')}
                                  />
                                </td>
                              )}
                              <td className="exercises-cell">
                                <div className="exercise-row">
                                  <Select
                                    options={exerciseOptions}
                                    value={
                                      row.exercise
                                        ? exerciseOptions.find((opt) => opt.value === row.exercise) || {
                                            value: row.exercise,
                                            label: row.exercise
                                          }
                                        : null
                                    }
                                    onChange={(option) => handleExerciseSelect(workoutIndex, rowIndex, option)}
                                    placeholder={t('trening.selectExercise')}
                                    isClearable
                                    classNamePrefix="react-select"
                                    menuPortalTarget={selectMenuProps.menuPortalTarget}
                                    menuPosition={selectMenuProps.menuPosition}
                                    styles={selectMenuProps.styles}
                                  />
                                  {row.exercise && (
                                    <button
                                      type="button"
                                      className="remove-exercise-btn"
                                      onClick={() => handleRemoveExerciseRow(workoutIndex, rowIndex)}
                                      aria-label={t('trening.removeExercise')}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                                {row.exercise && (
                                  <textarea
                                    className="exercise-note-input"
                                    rows={1}
                                    value={row.notes || ''}
                                    onChange={(e) => handleRowFieldChange(workoutIndex, rowIndex, 'notes', e.target.value)}
                                    placeholder={t('trening.exerciseNotePlaceholder')}
                                    aria-label={t('trening.exerciseNote')}
                                  />
                                )}
                              </td>
                              <td className="technique-cell">
                                <select
                                  className="technique-select"
                                  value={normalizeTechniqueKey(row.technique)}
                                  onChange={(e) => handleTechniqueChange(workoutIndex, rowIndex, e.target.value)}
                                  disabled={!row.exercise}
                                  aria-label={t('trening.exerciseTechnique')}
                                >
                                  {techniqueSelectOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="sets-cell">
                                <input
                                  type="text"
                                  value={row.sets}
                                  onChange={(e) => handleSetsChange(workoutIndex, rowIndex, e.target.value)}
                                  className="sets-input"
                                  placeholder={row.exercise ? t('trening.sets') : t('trening.addExerciseFirst')}
                                  disabled={!row.exercise}
                                />
                              </td>
                              <td className="reps-cell">
                                <input
                                  type="text"
                                  value={row.reps}
                                  onChange={(e) => handleRepsChange(workoutIndex, rowIndex, e.target.value)}
                                  className="reps-input"
                                  placeholder={row.exercise ? t('trening.reps') : t('trening.addExerciseFirst')}
                                  disabled={!row.exercise}
                                />
                              </td>
                              <td className="reps-cell rest-cell">
                                <input
                                  type="text"
                                  value={row.rest || ''}
                                  onChange={(e) => handleRowFieldChange(workoutIndex, rowIndex, 'rest', e.target.value)}
                                  className="reps-input"
                                  placeholder={row.exercise ? t('trening.restPlaceholder') : ''}
                                  disabled={!row.exercise}
                                />
                              </td>
                              {rowIndex === 0 && (
                                <td className="advice-cell" rowSpan={rows.length}>
                                  <textarea
                                    value={workout.advice}
                                    onChange={(e) => handleAdviceChange(workoutIndex, e.target.value)}
                                    className="advice-textarea"
                                    placeholder={t('trening.ptAdvicePlaceholder')}
                                    rows={rows.length > 1 ? 6 : 3}
                                  />
                                </td>
                              )}
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile card layout — outside table-container to avoid overflow-x clipping */}
                  <div className="mobile-workout-cards">
                    {visibleWorkouts.map((workout) => {
                      const workoutIndex = workouts.indexOf(workout);
                      if (workoutIndex === -1) return null;
                      return (
                        <div key={`${workout.workoutNumber}-${workout.variationKey}`} className="mobile-exercise-card">
                          <div className="mobile-variation-header">
                            <input
                              type="text"
                              className="mobile-variation-title-input"
                              value={workout.title}
                              onChange={(e) => handleWorkoutTitleChange(workoutIndex, e.target.value)}
                              placeholder={`${t('trening.workout')} ${workout.workoutNumber}${workout.variationKey}`}
                            />
                            {visibleWorkouts.length > 1 && (
                              <button
                                type="button"
                                className="remove-variation-btn"
                                onClick={() => handleRemoveWorkoutVariation(workoutIndex)}
                              >
                                {t('trening.removeVariation')}
                              </button>
                            )}
                          </div>
                          {workout.rows.map((row, rowIndex) => (
                            <div key={rowIndex} className="mobile-edit-exercise">
                              <div className="exercise-select-row">
                                <div>
                                  <Select
                                    options={exerciseOptions}
                                    value={row.exercise ? exerciseOptions.find((o) => o.value === row.exercise) || { value: row.exercise, label: row.exercise } : null}
                                    onChange={(option) => handleExerciseSelect(workoutIndex, rowIndex, option)}
                                    placeholder={t('trening.selectExercise')}
                                    isClearable
                                    classNamePrefix="react-select"
                                    menuPortalTarget={selectMenuProps.menuPortalTarget}
                                    menuPosition={selectMenuProps.menuPosition}
                                    styles={selectMenuProps.styles}
                                  />
                                </div>
                                {row.exercise && (
                                  <button type="button" className="remove-exercise-btn" onClick={() => handleRemoveExerciseRow(workoutIndex, rowIndex)} aria-label={t('trening.removeExercise')}>×</button>
                                )}
                              </div>
                              {row.exercise && (
                                <>
                                  <div className="mobile-row-fields mobile-row-fields-2">
                                    <div className="mobile-field">
                                      <label>{t('trening.sets')}</label>
                                      <input type="text" value={row.sets} onChange={(e) => handleSetsChange(workoutIndex, rowIndex, e.target.value)} placeholder={t('trening.sets')} />
                                    </div>
                                    <div className="mobile-field">
                                      <label>{t('trening.reps')}</label>
                                      <input type="text" value={row.reps} onChange={(e) => handleRepsChange(workoutIndex, rowIndex, e.target.value)} placeholder={t('trening.reps')} />
                                    </div>
                                    <div className="mobile-field">
                                      <label>{t('trening.rest')}</label>
                                      <input type="text" value={row.rest || ''} onChange={(e) => handleRowFieldChange(workoutIndex, rowIndex, 'rest', e.target.value)} placeholder={t('trening.restPlaceholder')} />
                                    </div>
                                    <div className="mobile-field">
                                      <label>{t('trening.exerciseTechnique')}</label>
                                      <select value={normalizeTechniqueKey(row.technique)} onChange={(e) => handleTechniqueChange(workoutIndex, rowIndex, e.target.value)}>
                                        {techniqueSelectOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                      </select>
                                    </div>
                                  </div>
                                  <div className="mobile-field mobile-note-field">
                                    <label>{t('trening.exerciseNote')}</label>
                                    <textarea rows={2} value={row.notes || ''} onChange={(e) => handleRowFieldChange(workoutIndex, rowIndex, 'notes', e.target.value)} placeholder={t('trening.exerciseNotePlaceholder')} />
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                          <div className="mobile-advice-row">
                            <label>{t('trening.ptAdvice')}</label>
                            <textarea value={workout.advice} onChange={(e) => handleAdviceChange(workoutIndex, e.target.value)} placeholder={t('trening.ptAdvicePlaceholder')} rows={3} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>

                  <div className="table-actions">
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={handleDownloadWorkoutImage}
                      disabled={isDownloading}
                    >
                      {isDownloading ? t('trening.preparingImage') : t('trening.downloadImage')}
                    </button>
                  </div>
                </>
              )}

              {!selectedCustomer && (
                <div className="no-customer-selected">
                  <p>{t('common.pleaseSelectCustomer')}</p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="workout-info">
                <p>{t('trening.workoutsPerWeek')} <strong>{workoutCount}</strong></p>
              </div>

              {workoutTips.length > 0 && (
                <div className="tips-section tips-section-readonly">
                  <div className="tips-header">
                    <h3 className="tips-title">{t('trening.tipsTitle')}</h3>
                  </div>
                  <div className="tips-cards">
                    {workoutTips.map((tip) => (
                      <div
                        key={tip.id}
                        className="tip-card tip-card-readonly"
                        onClick={() => setExpandedTip(expandedTip === tip.id ? null : tip.id)}
                      >
                        <div className="tip-card-header">
                          <span className="tip-title-readonly">{tip.title || t('trening.tipTitlePlaceholder')}</span>
                          <span className="tip-chevron">{expandedTip === tip.id ? '▲' : '▼'}</span>
                        </div>
                        {expandedTip === tip.id && (
                          <div className="tip-body-readonly">{tip.body}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="customer-selector">
                <label><strong>{t('trening.selectWorkoutSlot')}</strong></label>
                <Select
                  options={workoutSlotOptions}
                  value={
                    workoutSlotOptions.find((o) => o.value === selectedWorkoutNumber) ||
                    workoutSlotOptions[0] ||
                    null
                  }
                  onChange={(opt) => setSelectedWorkoutNumber(opt?.value ?? 1)}
                  placeholder={t('trening.chooseWorkoutSlot')}
                  isClearable={false}
                  className="customer-select"
                  classNamePrefix="react-select"
                  menuPortalTarget={selectMenuProps.menuPortalTarget}
                  menuPosition={selectMenuProps.menuPosition}
                  styles={selectMenuProps.styles}
                />
              </div>

              <div className="workout-plan-capture" ref={workoutTableRef}>
              {renderWarmupTable(true)}

              <div className="legend-card">
                <h3 className="legend-title">{t('trening.legendTitle')}</h3>
                <ul className="legend-list">
                  <li><strong>RM</strong> – {t('trening.legend.rm')}</li>
                  <li><strong>1RM / 3RM / 5RM</strong> – {t('trening.legend.rmVariants')}</li>
                  <li><strong>%1RM</strong> – {t('trening.legend.pct1rm')}</li>
                  <li><strong>AMRAP</strong> – {t('trening.legend.amrap')}</li>
                  <li><strong>RPE</strong> – {t('trening.legend.rpe')}</li>
                  <li><strong>RIR</strong> – {t('trening.legend.rir')}</li>
                  <li><strong>TUT</strong> – {t('trening.legend.tut')}</li>
                  <li><strong>ROM</strong> – {t('trening.legend.rom')}</li>
                  <li><strong>DS / DROPSET</strong> – {t('trening.legend.dropset')}</li>
                  <li><strong>BW</strong> – {t('trening.legend.bw')}</li>
                  <li><strong>SS / SUPERSET</strong> – {t('trening.legend.superset')}</li>
                  <li>{t('trening.legend.setTypeColumn')}</li>
                </ul>
              </div>

              <div className="table-container">
                <table className="trening-table">
                  <colgroup>
                    <col className="col-workout" />
                    <col className="col-exercises" />
                    <col className="col-technique" />
                    <col className="col-sets" />
                    <col className="col-reps" />
                    <col className="col-rest" />
                    <col className="col-advice" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t('trening.variation')}</th>
                      <th>{t('trening.exercises')}</th>
                      <th>{t('trening.exerciseTechnique')}</th>
                      <th>{t('trening.sets')}</th>
                      <th>{t('trening.reps')}</th>
                      <th>{t('trening.rest')}</th>
                      <th>{t('trening.ptAdvice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleWorkouts.flatMap((workout) => {
                      const workoutIndex = workouts.indexOf(workout);
                      if (workoutIndex === -1) return [];
                      const filledRows = workout.rows.filter((row) => row.exercise);
                      const rowsForDisplay = filledRows.length > 0 ? filledRows : [createEmptyRow()];
                      return rowsForDisplay.map((row, rowIndex) => (
                        <tr key={`${workout.variationKey}-display-${rowIndex}`}>
                          {rowIndex === 0 && (
                            <td className="workout-title-cell" rowSpan={rowsForDisplay.length}>
                              <span className="variation-label-readonly">
                                {workout.title?.trim() || `${t('trening.workout')} ${workout.workoutNumber}${workout.variationKey}`}
                              </span>
                            </td>
                          )}
                          <td className="exercises-cell read-only">
                            {row.exercise ? (
                              <span className="exercise-display-item">{row.exercise}</span>
                            ) : (
                              <span className="exercise-display-item muted">{t('trening.noExercises')}</span>
                            )}
                            {row.exercise && row.notes && (
                              <span className="exercise-note-display">{row.notes}</span>
                            )}
                          </td>
                          <td className="technique-cell read-only">
                            {row.exercise && normalizeTechniqueKey(row.technique) !== 'none' ? (
                              <span className="technique-tag-readonly">{getTechniqueLabel(t, row.technique)}</span>
                            ) : (
                              <span className="technique-empty">—</span>
                            )}
                          </td>
                          <td className="sets-cell read-only">
                            <span className="sets-display">{row.exercise ? row.sets || '—' : '—'}</span>
                          </td>
                          <td className="reps-cell read-only">
                            <span className="reps-display">{row.exercise ? row.reps || '—' : '—'}</span>
                          </td>
                          <td className="reps-cell read-only">
                            <span className="reps-display">{row.exercise ? row.rest || '—' : '—'}</span>
                          </td>
                          {rowIndex === 0 && (
                            <td className="advice-cell read-only" rowSpan={rowsForDisplay.length}>
                              <span className={workout.advice ? 'advice-display' : 'advice-display muted'}>
                                {workout.advice ? workout.advice : t('trening.noAdvice')}
                              </span>
                            </td>
                          )}
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>
              </div>

              {/* Mobile card layout for customer view */}
              <div className="mobile-workout-cards">
                {visibleWorkouts.map((workout) => {
                  const filledRows = workout.rows.filter((r) => r.exercise);
                  const rowsForDisplay = filledRows.length > 0 ? filledRows : [];
                  return (
                    <div key={`${workout.workoutNumber}-${workout.variationKey}`} className="mobile-exercise-card mobile-exercise-card-readonly">
                      <div className="mobile-card-section-header">
                        <span className="mobile-card-section-title">
                          {workout.title?.trim() || `${t('trening.workout')} ${workout.workoutNumber}${workout.variationKey}`}
                        </span>
                      </div>
                      {rowsForDisplay.length === 0 ? (
                        <p className="mobile-exercise-empty">{t('trening.noExercises')}</p>
                      ) : (
                        rowsForDisplay.map((row, i) => (
                          <div key={i} className="mobile-exercise-row">
                            <div className="mobile-exercise-head">
                              <span className="mobile-exercise-index">{i + 1}</span>
                              <span className="mobile-exercise-name">{row.exercise}</span>
                            </div>
                            <div className="mobile-exercise-details">
                              {normalizeTechniqueKey(row.technique) !== 'none' && (
                                <span className="technique-tag-readonly">{getTechniqueLabel(t, row.technique)}</span>
                              )}
                              {row.sets && row.sets.toString().trim() && (
                                <span className="mobile-metric-pill">{row.sets} {t('trening.sets').toLowerCase()}</span>
                              )}
                              {row.reps && row.reps.toString().trim() && (
                                <span className="mobile-metric-pill">{row.reps} {t('trening.reps').toLowerCase()}</span>
                              )}
                              {row.rest && row.rest.toString().trim() && (
                                <span className="mobile-metric-pill mobile-metric-rest">⏱ {row.rest} {t('trening.restSuffix')}</span>
                              )}
                            </div>
                            {row.notes && <div className="mobile-exercise-advice mobile-exercise-note">{row.notes}</div>}
                          </div>
                        ))
                      )}
                      {workout.advice && workout.advice.trim() && (
                        <div className="mobile-workout-advice">
                          <span className="mobile-workout-advice-label">{t('trening.ptAdvice')}</span>
                          {workout.advice}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="table-actions">
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={handleDownloadWorkoutImage}
                  disabled={isDownloading}
                >
                  {isDownloading ? t('trening.preparingImage') : t('trening.downloadImage')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sticky save button for mobile */}
      {isAdmin && selectedCustomer && (
        <div className="mobile-save-bar">
          <button onClick={handleSave} className="btn-primary">
            {t('common.saveChanges')}
          </button>
          {saved && <span className="saved-indicator">{t('common.saved')}</span>}
        </div>
      )}
    </div>
  );
}

export default Trening;
